import {
  SB_ID,
  PF_ID,
  usePriceFreezeSession,
} from '@/hooks/usePriceFreezeSession';
import { useToggle } from '@/hooks/useToggle';
import {
  useContactDetailsToShoppingBasketMutation,
  useCreatePriceFreeze,
  useCreateShoppingBasketMutation,
} from '@/hooks/GQL';
import {
  useIsAvailableOffer,
  useOffer,
  useSearchCriteriaSelector,
} from '@/providers/PriceFreezeOfferProvider';
import { useShowNotAvailable } from '@/providers/NotAvailableProvider';
import { selectPaxType } from '@/selectors';
import { useRepricingInfo } from '@/providers/RepricingInfoProvider';
import { createPriceFreezeRequest } from '@/components/PaxPage/createPriceFreezeRequest';

export const useHomePageAction = ({ onContinue }) => {
  const [, setSessionItem] = usePriceFreezeSession();
  const [error, toggleError] = useToggle(false);
  const available = useIsAvailableOffer();
  const { showPopup } = useShowNotAvailable();

  const { loading, mutate: createShoppingBasket } =
    useCreateShoppingBasketMutation({
      onSuccess({ data }) {
        setSessionItem(SB_ID, data.createShoppingBasket.shoppingBasketId);
        setSessionItem(PF_ID, null);
        onContinue(data.createShoppingBasket.shoppingBasketId);
      },
      onError: () => {
        toggleError();
      },
    });

  const _onContinue = () => {
    if (!available) {
      showPopup();
    } else {
      createShoppingBasket();
    }
  };

  return {
    loading,
    error,
    toggleError,
    onContinue: _onContinue,
    available,
  };
};

export function useAddContactDetailsAction({ onAddSuccess }) {
  const [error, toggleError] = useToggle();
  const [getSessionItem] = usePriceFreezeSession();

  const { loading, mutate } = useContactDetailsToShoppingBasketMutation({
    onSuccess() {
      onAddSuccess();
    },
    onError() {
      toggleError();
    },
  });

  const addContactDetails = (formData, shoppingBasketId) => {
    mutate({
      request: {
        shoppingId: shoppingBasketId || getSessionItem(SB_ID),
        name: formData.name,
        lastName: formData.surname,
        email: formData.email,
        address: 'PRICE_FREEZE_PLACEHOLDER',
        cityName: 'PRICE_FREEZE_PLACEHOLDER',
        countryPhoneNumber: formData.country_code.slice(1),
        countryCode: formData.country_code,
        phoneNumber: formData.phone,
      },
    });
  };

  return {
    loading,
    error,
    addContactDetails,
  };
}

export function useCreateProductAction({ onCreateSuccess }) {
  const [error, toggleError] = useToggle();
  const offer = useOffer();
  const paxType = useSearchCriteriaSelector(selectPaxType);
  const [getSessionItem, setSessionItem] = usePriceFreezeSession();

  const { repricing } = useRepricingInfo() || {};

  const { loading, mutate } = useCreatePriceFreeze({
    onSuccess({ data }) {
      setSessionItem(PF_ID, data.itineraryPriceFreeze);
      onCreateSuccess();
    },
    onError() {
      toggleError();
    },
  });

  const createProduct = (formData, shoppingBasketId) => {
    const request = createPriceFreezeRequest({
      formData,
      offer,
      paxType,
      shoppingBasketId: shoppingBasketId || getSessionItem(SB_ID),
      repricing,
    });
    mutate({ request });
  };

  return {
    error,
    loading,
    createProduct,
  };
}
